// The map editor's selection drawn in output px, so the projector shows what is being edited where it lands.
import { applyHomography, quadToQuad, rectQuad } from '~/lib/homography'
import { parsePath } from './path'
import { placements, surfaceQuad } from './placement'
import { sliceColor } from '.'

const LIT = '#78b4ff'
const CUT = '#ff6b6b'
const SELECTED = '#ffffff'
const CROSS = '#ffd400'
const BOX = 12
const RING = 24
// Cells per side of the slices grid.
const GRID = 8
const UNIT = rectQuad(0, 0, 1, 1)

const rgb = ([r, g, b]) => `rgb(${r} ${g} ${b})`

/** Draws `guides` (`selectShownGuides`) over `data` into `canvas`, scaled by `k` output px → canvas px; false when there is nothing to draw. */
export const drawGuides = (canvas, data, guides, k) => {
	const { width: W, height: H } = data.output
	const cw = Math.round(W * k)
	const ch = Math.round(H * k)
	if (canvas.width !== cw || canvas.height !== ch) Object.assign(canvas, { width: cw, height: ch })
	const c = canvas.getContext('2d')
	c.setTransform(1, 0, 0, 1, 0, 0)
	c.clearRect(0, 0, cw, ch)
	if (!guides) return false
	c.setTransform(k, 0, 0, k, 0, 0)
	c.lineJoin = 'round'

	const box = ([x, y], color) => {
		c.strokeStyle = color
		c.lineWidth = 2
		c.strokeRect(x - BOX / 2, y - BOX / 2, BOX, BOX)
	}
	const cross = ([x, y]) => {
		c.strokeStyle = CROSS
		c.lineWidth = 1
		c.setLineDash([])
		c.beginPath()
		c.moveTo(x, 0)
		c.lineTo(x, H)
		c.moveTo(0, y)
		c.lineTo(W, y)
		c.stroke()
		c.lineWidth = 2
		c.beginPath()
		c.arc(x, y, RING, 0, Math.PI * 2)
		c.stroke()
	}
	const quad = (q, color, selected) => {
		c.strokeStyle = color
		c.lineWidth = selected ? 3 : 1.5
		c.setLineDash(selected ? [] : [12, 8])
		c.beginPath()
		q.forEach(([x, y], i) => c[i ? 'lineTo' : 'moveTo'](x, y))
		c.closePath()
		c.stroke()
		c.setLineDash([])
	}

	const { tab, tool, selected, point, selection_ids = [], slices_grid } = guides
	const sel = id => selected?.id === id || selection_ids.includes(id)

	// Show slices grid: every placement outlined in its slice's colour, a grid across the selected slice's.
	if (slices_grid) {
		const line = (m, a, b) => {
			c.moveTo(...applyHomography(m, a))
			c.lineTo(...applyHomography(m, b))
		}
		for (const p of placements(data)) {
			c.strokeStyle = rgb(sliceColor(data, p.slice))
			c.lineWidth = 2
			c.beginPath()
			p.quad.forEach(([x, y], i) => c[i ? 'lineTo' : 'moveTo'](x, y))
			c.closePath()
			const m = p.slice === slices_grid.slice && quadToQuad(UNIT, p.quad)
			if (m) {
				for (let i = 1; i < GRID; i++) {
					line(m, [i / GRID, 0], [i / GRID, 1])
					line(m, [0, i / GRID], [1, i / GRID])
				}
				line(m, [0, 0], [1, 1])
				line(m, [1, 0], [0, 1])
			}
			c.stroke()
		}
	}

	if (tab === 'output' && tool === 'pin') {
		const corners = [
			[0, 0],
			[W, 0],
			[W, H],
			[0, H],
		]
		quad(corners, SELECTED, true)
		if (Number.isInteger(point) && corners[point]) cross(corners[point])
	} else if (tab === 'output') {
		for (const [id, s] of Object.entries(data.surfaces)) {
			if (s.hidden) continue
			c.strokeStyle = sel(id)
				? SELECTED
				: s.role === 'cut'
					? CUT
					: data.slices[s.slice]
						? rgb(sliceColor(data, s.slice))
						: LIT
			c.lineWidth = sel(id) ? 3 : 1.5
			c.stroke(new Path2D(s.path))
		}
		const s = selected?.kind === 'surface' && data.surfaces[selected.id]
		const slice = selected?.kind === 'slice' && !data.slices[selected.id]?.owner && selected.id
		const picture = slice
			? data.slices[slice]?.output
			: tool === 'picture' && s?.fit?.mode === 'own' && surfaceQuad(data, s)
		if (picture) {
			const color = rgb(sliceColor(data, slice || s.slice))
			quad(picture, color, true)
			for (const p of picture) box(p, color)
		} else if (s && !s.hidden) {
			const sps = parsePath(s.path)
			for (const sp of sps) for (const n of sp.nodes) box([n.x, n.y], SELECTED)
			const n = point && sps[point.sp]?.nodes[point.n]
			if (n) cross([n.x, n.y])
		}
	} else if (tab === 'input') {
		for (const p of placements(data)) quad(p.quad, rgb(sliceColor(data, p.slice)), sel(p.slice))
	}
	if (guides.cursor) cross(guides.cursor)
	return true
}
